import { tokenService } from './token-service'
import { createLoginFailedError, createSessionExpiredError, isAuthError } from './auth-errors'
import { identityServiceEndpoints } from './auth-client.http'
import { IframeAuthProxy } from './frame-proxy'
import authLogger from './auth-logger'

function parseAuthResponse(response) {
  return {
    accessToken: response.token || response.access_token,
    expiresAt: response.expires_at ?? response.expires,
    expiresIn: response.expires_in,
  }
}

/**
 * AuthClient handles core authentication operations for the appraiser frontend.
 * It owns token lifecycle (set/remove) but NOT user data — that lives in Redux.
 *
 * Operations route through the iframe auth proxy to the identity service.
 */
export class AuthClient {
  constructor() {
    this.proxy = new IframeAuthProxy()
  }

  /**
   * Validate the current session via the identity service STATUS endpoint.
   * Returns a fresh access token on success.
   */
  checkSession = async () => {
    authLogger.debug('checking session')

    try {
      const mountedToken = tokenService.getToken()
      const { accessToken, expiresIn } = parseAuthResponse(
        await identityServiceEndpoints.STATUS(this.proxy, mountedToken),
      )
      tokenService.setToken(accessToken)
      return { accessToken, expiresIn }
    } catch (error) {
      tokenService.removeToken()
      if (isAuthError(error)) throw error
      if (error.status === 401 || error.status === 403) {
        throw createLoginFailedError()
      }
      authLogger.error('check session failed:', error)
      throw createLoginFailedError()
    }
  }

  /**
   * Log in with username and password via the identity service.
   */
  login = async ({ username, password }) => {
    authLogger.debug('login from credentials')

    const result = await identityServiceEndpoints.LOGIN(this.proxy, username, password)
    const { accessToken, expiresIn } = parseAuthResponse(result)
    tokenService.setToken(accessToken)
    return { accessToken, expiresIn }
  }

  /**
   * Log in with a login code from the identity service.
   */
  loginWithCode = async (code) => {
    authLogger.debug('login with code')

    const result = await identityServiceEndpoints.LOGIN_CODE(this.proxy, code)
    const { accessToken, expiresIn } = parseAuthResponse(result)
    tokenService.setToken(accessToken)
    return { accessToken, expiresIn }
  }

  /**
   * Log in with a pre-signed impersonation token via the identity service.
   */
  impersonationLogin = async (token) => {
    authLogger.debug('impersonation login')

    const result = await identityServiceEndpoints.LOGIN_TOKEN(this.proxy, token)
    const { accessToken, expiresIn } = parseAuthResponse(result)
    tokenService.setToken(accessToken)
    return { accessToken, expiresIn }
  }

  /**
   * Refresh the bearer token via the identity service TOKEN endpoint.
   */
  refreshToken = async () => {
    authLogger.debug('refreshing token via proxy')
    try {
      const result = await identityServiceEndpoints.TOKEN(this.proxy)
      const { accessToken, expiresIn } = parseAuthResponse(result)
      tokenService.setToken(accessToken)
      return { accessToken, expiresIn }
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        tokenService.removeToken()
        throw createSessionExpiredError()
      }
      throw error
    }
  }

  /**
   * Log out via the identity service.
   */
  logout = async () => {
    authLogger.debug('logging out')
    const currentBearerToken = tokenService.getToken()

    try {
      await identityServiceEndpoints.LOGOUT(this.proxy, currentBearerToken)
    } catch (error) {
      authLogger.error('logout failed:', error)
    } finally {
      tokenService.removeToken()
    }
  }

  destroy() {
    if (this.proxy) {
      this.proxy.destroy()
      this.proxy = null
    }
  }
}
